// Sentry error monitoring — production-only.
// Initialized once from src/index.js before React mounts.
//
// Gating logic:
//   - Disabled in development (`yarn start`) — NODE_ENV !== 'production'
//   - Disabled in production builds unless REACT_APP_SENTRY_ENABLED === 'true'
//     and REACT_APP_SENTRY_DSN is provided.
//
// This means our local preview environment (which also runs a production build
// of the SPA but without these env vars set) stays silent.
import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';

const isEnabled =
  process.env.NODE_ENV === 'production' &&
  process.env.REACT_APP_SENTRY_ENABLED === 'true' &&
  !!process.env.REACT_APP_SENTRY_DSN;

export function initSentry() {
  if (!isEnabled) return;

  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_SENTRY_ENV || 'production',
    release: process.env.REACT_APP_RELEASE || undefined,
    integrations: [
      // Track route changes as transactions.
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      // Session Replay — anonymized: mask all text/inputs, block media.
      // Critical for an app handling KYC IDs, selfies, and PII.
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
    // 10% of normal sessions get a replay; 100% of erroring sessions do.
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Performance tracing — keep modest to control quota.
    tracesSampleRate: 0.1,
    // Never auto-attach IP / cookies / user agent.
    sendDefaultPii: false,
  });
}

export { Sentry };
